import { useState, useRef, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { useAllData } from "../../contexts/AllDataContext";
import { FlowState, JobStatus, FileWithStatus } from "./types";
import {
  ExtractedContract,
  RentRollItem,
  VerificationResult,
  ContractSaveError,
} from "../../types/contract";
import { 
  POLLING_INTERVAL_GENERAL_MS, 
  POLLING_INTERVAL_DETAILED_MS, 
  POLLING_JITTER_MS 
} from "./constants";
import { useContractManagement } from "./useContractManagement";
import { useBuildingCheck } from "./useBuildingCheck";
import { useDetailedJob } from "./useDetailedJob";
import { useVerification } from "./useVerification";
import { useRentRollFilter } from "./useRentRollFilter";
import { useJobPolling } from "./useJobPolling";
import { usePageUnloadWarning } from "./usePageUnloadWarning";
import { useSessionKeepAlive } from "./useSessionKeepAlive";
import { useFileDrop } from "./useFileDrop";
import { useUIEffects } from "./useUIEffects";
import { useGeneralExtraction } from "./useGeneralExtraction";
import { useDetailedExtraction } from "./useDetailedExtraction";
import { useFinalDataSave } from "./useFinalDataSave";
import { useContractEdit } from "./useContractEdit";
import { useContractNavigation } from "./useContractNavigation";
import { useContractConfirmation } from "./useContractConfirmation";
import { useStepManagement } from "./useStepManagement";
import apiClient from "../../api/axios";

export const useUploadPage = (
  flowState: FlowState,
  setFlowState: (state: FlowState) => void,
  handleNavigate: (path: string) => void
) => {
  const { forceRefresh } = useAllData();
  const authContext = useContext(AuthContext);
  const companyId = authContext?.companyId;
  
  // Refs
  const nextId = useRef(0);
  
  // 基本的な状態
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity?: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  // ファイル関連の状態
  const [fileLists, setFileLists] = useState<{
    valid: FileWithStatus[];
    invalid: FileWithStatus[];
  }>({ valid: [], invalid: [] });
  const [jobStatuses, setJobStatuses] = useState<Record<string, JobStatus>>({});

  // 抽出データ関連の状態
  const [generalContracts, setGeneralContracts] = useState<ExtractedContract[]>([]);
  const [rentRollData, setRentRollData] = useState<RentRollItem[]>([]);
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null);
  const [finalContracts, setFinalContracts] = useState<ExtractedContract[]>([]);
  const [aggregatedContracts, setAggregatedContracts] = useState<ExtractedContract[]>([]);

  // その他の状態
  const [unmatchedContractIds, setUnmatchedContractIds] = useState<Set<number>>(new Set());
  const [completedFilesForProgressBar, setCompletedFilesForProgressBar] = useState(0);
  const [totalFilesForProgressBar, setTotalFilesForProgressBar] = useState(0);
  
  // UI関連の状態
  const [showSummaryModal, setShowSummaryModal] = useState<boolean>(false);
  const [isSummaryDataReady, setIsSummaryDataReady] = useState<boolean>(false);
  const [failedUploads, setFailedUploads] = useState<string[]>([]);
  const [allFailedFilesForSummary, setAllFailedFilesForSummary] = useState<string[]>([]);
  
  // フィルター関連の状態
  const [rentRollPropertyFilter, setRentRollPropertyFilter] = useState("");
  const [rentRollRoomFilter, setRentRollRoomFilter] = useState("");
  const [rentRollLesseeFilter, setRentRollLesseeFilter] = useState("");
  
  // その他のUI状態
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [failedContractsDetails, setFailedContractsDetails] = useState<ContractSaveError[]>([]);
  const [showFailedContractsDialog, setShowFailedContractsDialog] = useState(false);
  const [filesWereDeleted, setFilesWereDeleted] = useState(false);
  const [selectedContract, setSelectedContract] = useState<ExtractedContract | null>(null);
  
  // Refs
  const detailScrollRef = useRef<HTMLDivElement>(null);

  // 他のhookを使用
  const detailedJob = useDetailedJob(
    flowState,
    setFlowState,
    setFinalContracts,
    POLLING_INTERVAL_DETAILED_MS,
    POLLING_JITTER_MS
  );
  
  const contractManagement = useContractManagement(
    new Map(), // buildingChecksは後で設定
    selectedContract,
    setSelectedContract,
    setAggregatedContracts,
    setFinalContracts,
    flowState,
    finalContracts
  );

  const buildingCheck = useBuildingCheck(
    finalContracts,
    flowState,
    selectedContract,
    contractManagement.sessionConfirmedData,
    contractManagement.setBeforeExistingDataMap,
    contractManagement.dbCheckedContracts,
    contractManagement.setDbCheckedContracts,
    contractManagement.setConflictResolutions,
    contractManagement.applyExistingData
  );
  
  // 検証hook
  useVerification(
    generalContracts,
    rentRollData,
    setUnmatchedContractIds,
    setVerificationResult
  );
  
  // レントロールフィルターhook
  const rentRollFilter = useRentRollFilter(
    rentRollData,
    rentRollPropertyFilter,
    rentRollRoomFilter,
    rentRollLesseeFilter
  );
  
  // ジョブポーリングhook
  useJobPolling(
    flowState,
    jobStatuses,
    setJobStatuses,
    generalContracts,
    setGeneralContracts,
    rentRollData,
    setRentRollData,
    fileLists,
    failedUploads,
    setSnackbar,
    setAllFailedFilesForSummary,
    setTotalFilesForProgressBar,
    setCompletedFilesForProgressBar,
    setIsSummaryDataReady,
    POLLING_INTERVAL_GENERAL_MS,
    POLLING_JITTER_MS
  );
  
  // ページ離脱警告hook
  usePageUnloadWarning(flowState);
  
  // セッション維持hook
  useSessionKeepAlive(flowState, apiClient);

  // ファイルドロップhook
  const fileDrop = useFileDrop(
    fileLists,
    setFileLists,
    setSnackbar,
    nextId,
    jobStatuses,
    setJobStatuses,
    setFilesWereDeleted,
    setRentRollData,
    setVerificationResult,
    setGeneralContracts
  );

  // UI効果hook
  useUIEffects(isSummaryDataReady, setShowSummaryModal);

  // 一般抽出hook
  const generalExtraction = useGeneralExtraction(
    jobStatuses,
    fileLists,
    filesWereDeleted,
    setError,
    setFlowState,
    setJobStatuses,
    setTotalFilesForProgressBar,
    setCompletedFilesForProgressBar,
    setIsSummaryDataReady,
    setFailedUploads
  );

  // 詳細抽出hook
  const detailedExtraction = useDetailedExtraction(
    companyId?.toString(),
    generalContracts,
    setError,
    setFlowState,
    setLoading,
    setFinalContracts,
    detailedJob.setDetailedJobIds,
    detailedJob.setDetailedJobStatuses
  );

  // 最終データ保存hook
  const finalDataSave = useFinalDataSave(
    finalContracts,
    rentRollData,
    jobStatuses,
    setSnackbar,
    setLoading,
    setFlowState,
    setError,
    setFailedContractsDetails,
    setShowFailedContractsDialog,
    forceRefresh,
    detailedJob.setChangedContractIds,
    contractManagement.setSessionConfirmedData,
    contractManagement.setDbCheckedContracts
  );

  // 契約編集hook
  const contractEdit = useContractEdit(
    generalContracts,
    setGeneralContracts,
    finalContracts,
    setFinalContracts,
    selectedContract,
    detailedJob.setChangedContractIds,
    setSnackbar
  );

  // 契約選択のハンドラー
  const handleSelectContract = useCallback((contract: ExtractedContract | null, shouldScroll: boolean) => {
    console.log("handleSelectContract called. shouldScroll:", shouldScroll, "contract ID:", contract?.id);
    setSelectedContract(contract);
    if (shouldScroll && detailScrollRef.current) {
      setTimeout(() => {
        detailScrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, []);

  // 契約ナビゲーションhook
  const contractNavigation = useContractNavigation(
    aggregatedContracts,
    handleSelectContract
  );

  // 契約確認hook
  const contractConfirmation = useContractConfirmation({
    selectedContract,
    finalContracts,
    setSelectedContract,
    setAggregatedContracts,
    setFinalContracts,
    sessionConfirmedData: contractManagement.sessionConfirmedData,
    setSessionConfirmedData: contractManagement.setSessionConfirmedData,
    confirmedContracts: contractManagement.confirmedContracts,
    setConfirmedContracts: contractManagement.setConfirmedContracts,
    needsReviewContracts: contractManagement.needsReviewContracts,
    setNeedsReviewContracts: contractManagement.setNeedsReviewContracts,
    buildingChecks: buildingCheck.buildingChecks,
    conflictResolutions: contractManagement.conflictResolutions,
    selectNextItem: contractNavigation.selectNextItem,
    setSnackbar,
    aggregatedContracts,
    handleSelectContract,
  });

  // ステップ管理hook
  const stepManagement = useStepManagement(flowState);

  // 状態リセット関数（基本状態のみ）
  const resetState = useCallback(() => {
    setFileLists({ valid: [], invalid: [] });
    setFlowState("initial");
    setLoading(false);
    setError(null);
    setGeneralContracts([]);
    setRentRollData([]);
    setVerificationResult(null);
    setFinalContracts([]);
    setAggregatedContracts([]);
    setUnmatchedContractIds(new Set());
    setCompletedFilesForProgressBar(0);
    setTotalFilesForProgressBar(0);
    setJobStatuses({});
    setSelectedContract(null);
    setIsSummaryDataReady(false);
    
    // 他のhookの状態もリセット
    contractManagement.resetContractState();
    buildingCheck.resetBuildingState();
    detailedJob.resetDetailedJobState();
  }, [
    setFileLists,
    setFlowState,
    setLoading,
    setError,
    setGeneralContracts,
    setRentRollData,
    setVerificationResult,
    setFinalContracts,
    setAggregatedContracts,
    setUnmatchedContractIds,
    setCompletedFilesForProgressBar,
    setTotalFilesForProgressBar,
    setJobStatuses,
    setSelectedContract,
    setIsSummaryDataReady,
    contractManagement,
    buildingCheck,
    detailedJob,
  ]);

  return {
    // 基本的な状態
    loading,
    setLoading,
    error,
    setError,
    snackbar,
    setSnackbar,
    
    // ファイル関連
    fileLists,
    setFileLists,
    jobStatuses,
    setJobStatuses,
    
    // 抽出データ関連
    generalContracts,
    setGeneralContracts,
    rentRollData,
    setRentRollData,
    verificationResult,
    setVerificationResult,
    finalContracts,
    setFinalContracts,
    aggregatedContracts,
    setAggregatedContracts,
    
    // その他
    unmatchedContractIds,
    setUnmatchedContractIds,
    completedFilesForProgressBar,
    setCompletedFilesForProgressBar,
    totalFilesForProgressBar,
    setTotalFilesForProgressBar,
    
    // UI関連
    showSummaryModal,
    setShowSummaryModal,
    isSummaryDataReady,
    setIsSummaryDataReady,
    failedUploads,
    setFailedUploads,
    allFailedFilesForSummary,
    setAllFailedFilesForSummary,
    
    // フィルター関連
    rentRollPropertyFilter,
    setRentRollPropertyFilter,
    rentRollRoomFilter,
    setRentRollRoomFilter,
    rentRollLesseeFilter,
    setRentRollLesseeFilter,
    
    // その他のUI状態
    showResetConfirm,
    setShowResetConfirm,
    failedContractsDetails,
    setFailedContractsDetails,
    showFailedContractsDialog,
    setShowFailedContractsDialog,
    filesWereDeleted,
    setFilesWereDeleted,
    selectedContract,
    setSelectedContract,
    detailScrollRef,
    
    // Context values
    companyId,
    forceRefresh,
    nextId,
    
    // 契約選択ハンドラー
    handleSelectContract,
    
    // 状態リセット関数
    resetState,
    
    // 他のhookからの状態
    ...contractManagement,
    ...buildingCheck,
    ...detailedJob,
    
    
    // レントロールフィルター
    ...rentRollFilter,
    
    // ファイルドロップ
    ...fileDrop,
    
    // 一般抽出
    ...generalExtraction,
    
    // 詳細抽出
    ...detailedExtraction,
    
    // 最終データ保存
    ...finalDataSave,
    
    // 契約編集
    ...contractEdit,
    
    // 契約ナビゲーション
    ...contractNavigation,
    
    // 契約確認
    ...contractConfirmation,
    
    // ステップ管理
    ...stepManagement,
  };
};